import {
  ColumnLayout,
  ExpandableSection,
  FormField,
  Input,
  Select,
  SpaceBetween,
  Textarea,
  type SelectProps,
} from '@cloudscape-design/components';
import type { CodeDiffState, DiffSource, FieldErrors } from './evaluationSubjects';

const DIFF_SOURCE_OPTIONS: SelectProps.Option[] = [
  { label: 'Paste the diff text', value: 'rawDiff' },
  { label: 'Pre-signed S3 URL', value: 'diffURL' },
];

interface Props {
  value: CodeDiffState;
  onChange: (patch: Partial<CodeDiffState>) => void;
  errors: FieldErrors;
}

export function CodeDiffFields({ value, onChange, errors }: Props) {
  const selectedSource =
    DIFF_SOURCE_OPTIONS.find((option) => option.value === value.diffSource) ?? DIFF_SOURCE_OPTIONS[0];

  return (
    <SpaceBetween size="l">
      <FormField
        label="Diff source"
        description="Paste the diff if you have the text; use an S3 URL for a diff too large to paste."
      >
        <Select
          selectedOption={selectedSource}
          onChange={({ detail }) => onChange({ diffSource: detail.selectedOption.value as DiffSource })}
          options={DIFF_SOURCE_OPTIONS}
        />
      </FormField>

      {value.diffSource === 'rawDiff' ? (
        <FormField
          label="Diff content"
          description="The output of git diff, starting with a line like: diff --git a/src/file.ts b/src/file.ts"
          constraintText="Required"
          errorText={errors.rawDiff}
        >
          <Textarea
            value={value.rawDiff}
            onChange={({ detail }) => onChange({ rawDiff: detail.value })}
            placeholder="diff --git a/src/file.ts b/src/file.ts"
            rows={10}
          />
        </FormField>
      ) : (
        <FormField
          label="Diff URL"
          description="A pre-signed S3 URL Arbiter can download the diff from once."
          constraintText="Required. An https S3 URL."
          errorText={errors.diffURL}
        >
          <Input
            value={value.diffURL}
            onChange={({ detail }) => onChange({ diffURL: detail.value })}
            placeholder="https://my-bucket.s3.us-west-2.amazonaws.com/diff.patch"
          />
        </FormField>
      )}

      <FormField
        label="Package name"
        description="The Brazil package the diff belongs to. Arbiter uses it to read the surrounding code the diff does not include."
        constraintText="Required"
        errorText={errors.diffPackageName}
      >
        <Input
          value={value.packageName}
          onChange={({ detail }) => onChange({ packageName: detail.value })}
          placeholder="MyServicePackage"
        />
      </FormField>

      <ExpandableSection headerText="More context (optional)" variant="footer">
        <SpaceBetween size="l">
          <ColumnLayout columns={2}>
            <FormField
              label="Base commit"
              description="The commit the diff was generated against. Arbiter checks the package out here to read file context."
              constraintText="Optional. A full 40-character hex SHA."
            >
              <Input
                value={value.baseCommitId}
                onChange={({ detail }) => onChange({ baseCommitId: detail.value })}
                placeholder="e3b0c44298fc1c149afbf4c8996fb92427ae41e4"
              />
            </FormField>
            <FormField label="Branch" description="The branch the change is on." constraintText="Optional">
              <Input
                value={value.branch}
                onChange={({ detail }) => onChange({ branch: detail.value })}
                placeholder="mainline"
              />
            </FormField>
          </ColumnLayout>
          <FormField
            label="Version set"
            description="The Brazil version set the package builds in."
            constraintText="Optional"
          >
            <Input
              value={value.versionSet}
              onChange={({ detail }) => onChange({ versionSet: detail.value })}
              placeholder="MyVersionSet/mainline"
            />
          </FormField>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
}
